import KoobTooltip from '@koob/ui/src/components/KoobTooltip';
import { resyncExtrasDayIndex } from '../logic/resyncExtrasDayIndex';
import { getDayInsertBetweenState } from '../logic/getDayInsertBetweenState';
import { useIsDayEditable } from '../hooks/useIsDayEditable';
import { useHasPartnerDayInRange } from '../hooks/useHasPartnerDayInRange';
import useTripStore from '../store/useTripStore';
import {
  useDays,
  useExperience,
  useTripFilters,
  useSetTripFilters,
} from '../hooks/useTripStoreSelectors';

interface TripDayInsertBetweenProps {
  dayIndex: number;
  position: 'before' | 'after';
  experience?: any;
  isDisabled?: boolean;
  translation: (key: string) => string;
  // DMC-only: a draft trip may confirm a partner price shift before
  // committing the insert/remove; TO never passes it, so the edit always
  // proceeds.
  confirmPartnerPriceShifts?: (newDays: any[]) => Promise<boolean> | boolean;
}

export default function TripDayInsertBetween({
  dayIndex,
  position,
  experience,
  isDisabled,
  translation,
  confirmPartnerPriceShifts,
}: TripDayInsertBetweenProps) {
  const tripFilters = useTripFilters();
  const setTripFilters = useSetTripFilters();
  const days = useDays();
  const experiences = useTripStore((state) => state.experiences);
  const updateDays = useTripStore((state) => state.updateDays);
  const previousExperience = useExperience(dayIndex - 1);
  const currentDayEditable = useIsDayEditable(days[dayIndex], experiences);
  const previousDayEditable = useIsDayEditable(days[dayIndex - 1], experiences);
  const hasPartnerDayInRange = useHasPartnerDayInRange(days, experiences);

  const { targetIndex, shouldRemove, shouldRender } = getDayInsertBetweenState({
    position,
    dayIndex,
    experience,
    previousExperience,
    days,
    tripDuration: tripFilters?.tripDuration,
    isBookedTrip: Boolean(tripFilters?.isBookedTrip),
    currentDayEditable,
    previousDayEditable,
    hasPartnerDayInRange,
  });

  const addDay = async () => {
    const newDays = [...(days ?? [])];

    if (newDays.length === 0) {
      for (let i = 0; i < tripFilters?.tripDuration; i++) {
        newDays.push({ experienceId: null, hotelId: null, selectedExtras: [] });
      }
    }

    newDays.splice(targetIndex, 0, {
      experienceId: null,
      hotelId: null,
      selectedExtras: [],
    });

    const confirmed = await confirmPartnerPriceShifts?.(newDays);
    if (confirmed === false) {
      return;
    }

    updateDays(resyncExtrasDayIndex(newDays));

    setTripFilters((current) => ({
      ...current,
      tripDuration: (current?.tripDuration ?? 0) + 1,
    }));
  };

  const removeDay = async () => {
    const newDays = [...(days ?? [])];

    newDays.splice(targetIndex, 1);

    const confirmed = await confirmPartnerPriceShifts?.(newDays);
    if (confirmed === false) {
      return;
    }

    updateDays(resyncExtrasDayIndex(newDays));

    setTripFilters((current) => ({
      ...current,
      tripDuration: Math.max(1, (current?.tripDuration ?? 1) - 1),
    }));
  };

  return shouldRender ? (
    <KoobTooltip
      tip={translation('compose.removeDayTooltipMessage')}
      position="top"
      block
      disabled={!isDisabled}
    >
      <div
        onClick={() => {
          if (shouldRemove) {
            if (!isDisabled) {
              removeDay();
            }
          } else {
            addDay();
          }
        }}
        className={[
          'relative group cursor-pointer',
          // The "+" is 24px and centred on the rule, so it eats 12px of the
          // gutter on each side: 28px of padding is what leaves real air
          // between the button and the experience card below it.
          shouldRemove ? 'py-2' : 'py-7',
        ].join(' ')}
      >
        <div
          className={[
            'w-full h-1 bg-gray-200 transition rounded-full',
            shouldRemove ? 'group-hover:bg-red-500' : 'group-hover:bg-k-orange',
          ].join(' ')}
        >
          <div
            className={[
              'absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-gray-200 transition rounded-full flex justify-center items-center',
              shouldRemove
                ? 'group-hover:bg-red-500'
                : 'group-hover:bg-k-orange',
            ].join(' ')}
          >
            {shouldRemove ? (
              <i className="fa fa-minus text-gray-400 group-hover:text-white transition" />
            ) : (
              <i className="fa fa-plus text-gray-400 group-hover:text-white transition" />
            )}
          </div>
        </div>
      </div>
    </KoobTooltip>
  ) : null;
}
